import { useEffect, useMemo, useState } from "react";
import { useUser } from "../../../context/userContext";
import { handlePriceWithDate, handlePriceWithRM } from "../../../utils/priceUtils";
import { convertDateTimeToDateString } from "../../../utils/datetimeUtils";
import ReusableTable from "../reusableTable";
import { Chip } from "@mui/material";

export default function GeneralCommissionProgressList({
    commPayType,
    fetchApi,
    withUserId,
    onRowClick
}) {
    const { user } = useUser();
    const [commissionList, setCommissionList] = useState([]);

    const statusMap = {
        'In Progress': { label: 'In Progress', color: 'info' },
        'Complete': { label: 'Complete', color: 'success' },
    }

    const fetchCommissionProgress = async () => {
        if (user?.userId === undefined || user?.userId === null) {
            return;
        }

        let response;
        try {
            // const response = await getCommissionProgressByAgentId(user.userId);
            if (withUserId) {
                response = await fetchApi(user.userId);
            }
            else {
                response = await fetchApi();
            }

            if (!commPayType) {
                setCommissionList(response.data);
            }
            else {
                setCommissionList(response.data.filter(item => item?.commissionProgressDto?.commPayType.startsWith(commPayType)));
            }
        }
        catch (error) {
            console.error("Error fetching commission data:", error);
        }
        finally {
        }
    };

    useEffect(() => {
        fetchCommissionProgress();
    }, [commPayType, fetchApi, user?.userId]);

    const columns = useMemo(() => [
        {
            id: 'depositDate',
            header: 'Deposit Date',
            // accessorFn: row => convertDateTimeToDateString(row.depositOn),
            accessorFn: row => row.salesDetailsForCommissionProgress.depositOn ? new Date(row.salesDetailsForCommissionProgress.depositOn).getTime() : null,
            Cell: ({ row }) => convertDateTimeToDateString(row.original.salesDetailsForCommissionProgress.depositOn),
        },
        {
            id: 'Customer',
            header: 'Customer',
            accessorKey: 'salesDetailsForCommissionProgress.customerName',
        },
        {
            id: 'ProductEnergy',
            header: 'Product Energy',
            accessorFn: row =>
                `${row.salesDetailsForCommissionProgress.energy ?? ""} kWp`.trim(),
        },
        {
            id: 'oriPrice',
            header: 'Ori Price',
            accessorFn: row => row.salesDetailsForCommissionProgress.originalPrice,
            Cell: ({ row }) => {
                const cp = row.original.salesDetailsForCommissionProgress;
                if (!cp || cp.originalPrice == null) {
                    return "";
                }

                return handlePriceWithRM(cp.originalPrice);
            },
        },
        {
            id: 'asternDiscount',
            header: 'Astern Disc.',
            accessorFn: row => row.salesDetailsForCommissionProgress.discountByAstern,
            Cell: ({ row }) => {
                const cp = row.original.salesDetailsForCommissionProgress;
                if (!cp || cp.discountByAstern == null) {
                    return "";
                };

                return handlePriceWithRM(cp.discountByAstern);
            },
        },
        {
            id: 'agentDiscount',
            header: 'Agent Disc.',
            accessorFn: row => row.salesDetailsForCommissionProgress.discountByAgent,
            Cell: ({ row }) => {
                const cp = row.original.salesDetailsForCommissionProgress;
                if (!cp || cp.discountByAgent == null) {
                    return "";
                };

                return handlePriceWithRM(cp.discountByAgent);
            },
        },
        {
            id: 'finalPrice',
            header: 'Final Price',
            accessorFn: row => row.salesDetailsForCommissionProgress.finalPrice,
            Cell: ({ row }) => {
                const cp = row.original.salesDetailsForCommissionProgress;
                if (!cp || cp.finalPrice == null) {
                    return null
                };

                return handlePriceWithRM(
                    cp.finalPrice
                );
            },
        },
        {
            id: 'Agent',
            header: 'Agent',
            accessorKey: 'salesDetailsForCommissionProgress.agentName',
        },
        {
            id: 'CoAgent',
            header: 'CoAgent',
            accessorFn: row => row.salesDetailsForCommissionProgress.coAgentName,
        },
        {
            id: 'Commission Type',
            header: 'Comm Type',
            accessorKey: 'commissionProgressDto.commPayType',
        },
        {
            id: 'Commission Amount',
            header: 'Commission',
            accessorFn: row => row?.commissionProgressDto.commPay,
            Cell: ({ row }) => {
                const cp = row.original?.commissionProgressDto;
                if (!cp || cp.commPay == null) {
                    return null
                };

                return handlePriceWithRM(
                    cp.commPay
                );
            },
        },
        {
            id: 'First Payment',
            header: 'First Payment',
            accessorFn: row => row.commissionProgressDto.firstPayment,
            Cell: ({ row }) => {
                const cp = row.original.commissionProgressDto;
                if (!cp || cp.firstPayment == null) {
                    return null
                };

                return handlePriceWithDate(
                    cp.firstPayment,
                    cp.firstPaymentOn
                );
            },
        },
        {
            id: 'Second Payment',
            header: 'Second Payment',
            accessorFn: row => row.commissionProgressDto.secondPayment,
            Cell: ({ row }) => {
                const cp = row.original.commissionProgressDto;
                if (!cp || cp.secondPayment == null) {
                    return null
                };

                return handlePriceWithDate(
                    cp.secondPayment,
                    cp.secondPaymentOn
                );
            },
        },
        {
            id: 'Payment Status',
            header: 'Payment Status',
            // accessorKey: 'commissionProgressDto.paymentStatus',
            accessorFn: row => row?.commissionProgressDto?.paymentStatus,
            Cell: ({ row }) => {
                const status = row.original?.commissionProgressDto?.paymentStatus;
                if (!status) {
                    return <Chip color={'default'} label={'Unknown'} size="small" />;
                }

                const { label, color } = statusMap[status] ?? { label: status, color: 'default' };
                return <Chip color={color} label={label} size="small" />;
            },
        },
        {
            id: 'Repayment',
            header: 'Repayment',
            accessorFn: row => row.commissionProgressDto.repayment,
            Cell: ({ row }) => {
                const cp = row.original.commissionProgressDto;
                if (!cp || cp.repayment == null) {
                    return null
                };

                return handlePriceWithDate(
                    cp.repayment,
                    cp.repaymentOn
                );
            },
        },
        {
            id: 'Repayment Status',
            header: 'Repayment Status',
            // accessorKey: 'commissionProgressDto.repaymentStatus',
            accessorFn: row => row?.commissionProgressDto?.repaymentStatus,
            Cell: ({ row }) => {
                const status = row.original?.commissionProgressDto?.repaymentStatus;
                if (!status) {
                    return <Chip color={'default'} label={'Unknown'} size="small" />;
                }

                const { label, color } = statusMap[status] ?? { label: status, color: 'default' };
                return <Chip color={color} label={label} size="small" />;
            },
        },
        {
            id: 'Updated By',
            header: 'Updated By',
            accessorKey: 'updatedBy',
        },
        {
            id: 'Updated On',
            header: 'Date',
            filterVariant: 'datetime-range',
            accessorFn: (row) => new Date(row.updatedOn),
            Cell: ({ row }) => {
                const cp = row.original;

                if (!cp) {
                    return "";
                }
                return convertDateTimeToDateString(cp.updatedOn);
            },
        }
    ],
        []
    );

    return (
        <ReusableTable
            columns={columns}
            data={commissionList}
            onRowClick={onRowClick}
        >
        </ReusableTable>
    );
}